import { Service } from '@tino-frontend/adapters'
import {
  type DownloadSupplierStatementCSVResponse,
  type MerchantContact,
  type MerchantIsAbleToTransactParams,
  type MerchantIsAbleToTransactResponse,
  type MerchantProfile,
  type MerchantRatings,
  type StatementsQuery,
  type SupplierMaxDueDaysResponse,
  type RatingsResumeResponse,
  type Places,
  type MerchantSocialMedia,
  type MerchantChanges,
  type MerchantProfileId,
  BureauMerchantAddresses,
} from '@types'

import { UserState } from 'modules/user/stores'

export class SupplierService {
  private statementPath = 'statements-csv'
  private accountingStatementPath = 'accounting-statements-csv'
  private merchants = 'merchants'

  constructor(
    private readonly http: Service,
    private readonly supplierBff: Service
  ) {}

  async downloadStatementsCSV(
    supplierId: string,
    params: Partial<StatementsQuery>
  ): Promise<string> {
    const { download_url } = await this.http.get<
      unknown,
      DownloadSupplierStatementCSVResponse
    >(`/suppliers/${supplierId}/${this.statementPath}`, { params })

    return await this.http.get<unknown, string>(download_url, {
      responseType: 'blob',
    })
  }

  async downloadAccountingStatementsCSV(
    supplierId: string,
    params: Partial<StatementsQuery>
  ): Promise<string> {
    const { download_url } = await this.http.get<
      unknown,
      DownloadSupplierStatementCSVResponse
    >(`/suppliers/${supplierId}/${this.accountingStatementPath}`, { params })

    return await this.http.get<unknown, string>(download_url, {
      responseType: 'blob',
    })
  }

  /**
   * @deprecated use getMerchant instead
   */
  async getMerchantIsAbleToTransact(
    supplierId: string,
    { merchant_document_number }: MerchantIsAbleToTransactParams
  ): Promise<MerchantIsAbleToTransactResponse> {
    return this.supplierBff.get(
      `v1/suppliers/${supplierId}/${this.merchants}/${merchant_document_number}`
    )
  }

  async getMerchant(
    supplierId: string,
    merchantDocumentNumber: string,
    origin: string
  ): Promise<MerchantProfile> {
    return this.supplierBff.get(
      `v1/suppliers/${supplierId}/${this.merchants}/${merchantDocumentNumber}`,
      { headers: { 'x-origin': origin } }
    )
  }

  async getMerchantContacts(
    supplierId: string,
    merchantDocumentNumber: string
  ): Promise<{ contacts: MerchantContact[] }> {
    return this.supplierBff.get(
      `v1/suppliers/${supplierId}/${this.merchants}/${merchantDocumentNumber}/contacts`
    )
  }

  async getMaxDueDays(supplierId: string): Promise<SupplierMaxDueDaysResponse> {
    return this.supplierBff.get(`/v1/suppliers/${supplierId}/max-due-days`)
  }

  async getMaxDueDaysWithMerchant(
    supplierId: string,
    merchantDocumentNumber: string
  ): Promise<SupplierMaxDueDaysResponse> {
    return this.supplierBff.get(
      `/v1/suppliers/${supplierId}/merchants/${merchantDocumentNumber}/max-due-days`
    )
  }

  async getMerchantId(
    supplierId: string,
    merchantDocumentNumber: string
  ): Promise<MerchantProfileId> {
    return this.supplierBff.get(
      `v2/suppliers/${supplierId}/${this.merchants}/${merchantDocumentNumber}`
    )
  }

  async getMerchantProfile(
    supplierId: string,
    merchantDocumentNumber: string
  ): Promise<MerchantRatings> {
    return this.supplierBff.get(
      `v1/merchant-profile?merchant_document_number=${merchantDocumentNumber}&supplier_id=${supplierId}`
    )
  }

  async getPlacePhotos(address: string, placeName: string): Promise<Places> {
    return this.supplierBff.get(`v1/places`, {
      params: {
        address,
        place_name: placeName,
      },
    })
  }

  async getMerchantSocialMedias(
    companyName: string,
    merchantDocumentNumber: string,
    address: string
  ): Promise<MerchantSocialMedia[]> {
    return this.supplierBff.get(
      `/v1/social-media?company_name=${companyName}&company_document_number=${merchantDocumentNumber}&address=${address}`
    )
  }

  async getRatingsResume({
    ratings,
    user,
  }: {
    ratings: MerchantRatings
    user: UserState
  }): Promise<RatingsResumeResponse> {
    return this.http.post(
      'https://tino.app.n8n.cloud/webhook/a35110ec-aadb-4bd4-9de8-54a5ba61a16b',
      {
        ratings,
        user,
      }
    )
  }

  async getMerchantChanges(
    merchantDocumentNumber: string
  ): Promise<MerchantChanges> {
    return this.supplierBff.get(
      `v1/companies/${merchantDocumentNumber}/registration-changes`
    )
  }

  async getMerchantAddress(cnpj: string): Promise<BureauMerchantAddresses> {
    return this.supplierBff.get(`v2/merchants/address?document_number=${cnpj}`)
  }
}
